import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useResetFormOnLoad } from '~/hooks/useResetFormOnLoad';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';
import {
  type CreateLedgerAccountPayload,
  type LedgerAccount,
  type UpdateLedgerAccountPayload,
  useMutationCreateLedgerAccount,
  useMutationDeleteLedgerAccount,
  useMutationUpdateLedgerAccount,
  useQueryLedgerAccount,
  zodLedgerAccountTypeSchema,
} from '~/data/ledgerAccount';

import ToastService from '~/services/toast.service';

import {
  logFormChangesAndErrors,
  withoutObjectKeysWhereValueIs,
} from '@vestigas/utils';
import { i18n, log } from '~/utils';

import { useAppForm } from '~/ui/molecules/TanstackForm';

import {
  type LedgerAccountDuplicateCheckFieldName,
  runLedgerAccountDuplicateCheck,
} from './runLedgerAccountDuplicateCheck';

// Swedish date ordering matches the API's YYYY-MM-DD format without converting to UTC.
const serverDateFormatter = new Intl.DateTimeFormat('sv-SE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const zodLedgerAccountFormSchema = z.object({
  accountType: z.union([zodLedgerAccountTypeSchema, z.literal('')]).nullable(),
  companyId: z.uuid({
    message: i18n.t(($) => $.form.errors.companyIsRequired, {
      ns: 'ledgerAccounts',
    }),
  }),
  costType: z.string().optional(),
  name: z.string().optional(),
  number: z.string().trim().min(1),
  validFrom: z.iso.date({
    message: i18n.t(($) => $.form.errors.validFromIsRequired, {
      ns: 'ledgerAccounts',
    }),
  }),
});

type LedgerAccountFormValues = z.infer<typeof zodLedgerAccountFormSchema>;

const EMPTY_FORM_VALUES: LedgerAccountFormValues = {
  accountType: 'expense',
  companyId: '',
  costType: '',
  name: '',
  number: '',
  validFrom: '',
};

function getDefaultValues(
  ledgerAccount: LedgerAccount | undefined,
): LedgerAccountFormValues {
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = serverDateFormatter.format(tomorrowDate);

  if (!ledgerAccount) {
    return {
      ...EMPTY_FORM_VALUES,
      validFrom: tomorrow,
    };
  }

  return {
    accountType: ledgerAccount.accountType,
    companyId: ledgerAccount.company.id,
    costType: ledgerAccount.costType ?? '',
    name: ledgerAccount.name,
    number: ledgerAccount.number,
    validFrom: ledgerAccount.validFrom || tomorrow,
  };
}

/**
 * Create takes `companyId`/`number`/`name`/`accountType` as required, so an
 * empty `accountType` is omitted rather than sent as `''`, letting the
 * payload schema report it as missing instead of an invalid enum value.
 */
function buildCreatePayload(
  value: LedgerAccountFormValues,
): CreateLedgerAccountPayload {
  return withoutObjectKeysWhereValueIs(
    {
      accountType: value.accountType || undefined,
      companyId: value.companyId,
      costType: value.costType,
      name: value.name,
      number: value.number,
      validFrom: value.validFrom,
    },
    [undefined],
  ) as CreateLedgerAccountPayload;
}

/**
 * Update has no immutable `companyId`/`number` fields and, unlike create,
 * must distinguish "leave unchanged" from "clear": the three optional
 * fields are always sent, as either their new value or an explicit `null`,
 * because omitting a field here means the API leaves it untouched.
 */
function buildUpdatePayload(
  value: LedgerAccountFormValues,
): UpdateLedgerAccountPayload {
  return {
    accountType: value.accountType || null,
    costType: value.costType || null,
    name: value.name || null,
    validFrom: value.validFrom,
  };
}

type P = {
  readonly ledgerAccountId?: UUID;
  readonly onSuccess: () => void;
};

/**
 * Owns create/edit for one ledger account version: loading the account behind
 * a deep-linked id, form state, collision handling, and persistence mutations.
 */
export function useLedgerAccountForm({ ledgerAccountId, onSuccess }: P) {
  const { t } = useTranslation('ledgerAccounts');

  const autoFocusInputRef = useRef<HTMLInputElement>(null);

  const [collisionError, setCollisionError] = useState<string | undefined>();

  const queryClient = useQueryClient();

  const lastDuplicateCheckFieldRef = useRef<
    LedgerAccountDuplicateCheckFieldName | undefined
  >(undefined);

  const markDuplicateCheckFieldTouched = useCallback(
    (field: LedgerAccountDuplicateCheckFieldName) => {
      lastDuplicateCheckFieldRef.current = field;
    },
    [],
  );

  // Edit mode is driven by the id, not the fetched account, so the form
  // renders with the right disabled fields and mutation immediately -- it
  // does not flip once loading resolves.
  const isEditMode = Boolean(ledgerAccountId);

  const {
    data: ledgerAccount,
    isError: queryError,
    isLoading,
  } = useQueryLedgerAccount(ledgerAccountId);

  const isDeleted = ledgerAccount?.isDeleted === true;

  const createMutation = useMutationCreateLedgerAccount({
    meta: {
      suppressErrorForErrorCodes: [409],
    },
  });
  const updateMutation = useMutationUpdateLedgerAccount({
    meta: {
      suppressErrorForErrorCodes: [409],
    },
  });
  const deleteMutation = useMutationDeleteLedgerAccount({
    onSuccess() {
      ToastService.success(t(($) => $.form.toast.deleteSuccess));
      onSuccess();
    },
  });

  const mutationError = createMutation.error || updateMutation.error;
  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleDelete() {
    if (!ledgerAccountId || isDeleted) {
      return;
    }

    deleteMutation.mutate(ledgerAccountId);
  }

  const defaultValues = getDefaultValues(ledgerAccount);

  const form = useAppForm({
    defaultValues,
    async onSubmit({ value }) {
      if (isDeleted) {
        return;
      }

      const validatedValue = zodLedgerAccountFormSchema.parse(value);

      setCollisionError(undefined);

      try {
        if (ledgerAccount) {
          await updateMutation.mutateAsync({
            ledgerAccountId: ledgerAccount.id,
            payload: buildUpdatePayload(validatedValue),
          });
          ToastService.success(t(($) => $.form.toast.updateSuccess));
        } else {
          await createMutation.mutateAsync(buildCreatePayload(validatedValue));
          ToastService.success(t(($) => $.form.toast.createSuccess));
        }

        onSuccess();
      } catch (error) {
        if (
          error instanceof VestigasHTTPError &&
          error.response.status === 409
        ) {
          setCollisionError(t(($) => $.form.errors.collision));
        }
      }
    },
    validators: {
      // Form-level validator, so it fires on every field's blur, not just
      // the three fields the uniqueness check depends on. Consume the ref
      // on use so a later blur on an unrelated field (name, cost type,
      // account type) doesn't replay the check for whichever field was
      // last edited.
      onBlurAsync: ({ value }) => {
        const changedField = lastDuplicateCheckFieldRef.current;
        lastDuplicateCheckFieldRef.current = undefined;

        return runLedgerAccountDuplicateCheck(
          queryClient,
          {
            companyId: value.companyId,
            number: value.number,
            validFrom: value.validFrom,
          },
          { changedField },
        );
      },
      onChange: zodLedgerAccountFormSchema,
      onMount: zodLedgerAccountFormSchema,
    },
    ...logFormChangesAndErrors('LedgerAccountForm', log),
  });

  // Create mode has no query to wait for: `defaultValues` is already correct
  // at mount, and resetting anyway would clear the onMount validation result
  // that keeps Speichern disabled on an empty form. Treat it as perpetually
  // "still loading" so the reset-once effect never fires.
  useResetFormOnLoad(form, defaultValues, !ledgerAccountId || isLoading);

  useEffect(() => {
    // Use requestAnimationFrame to wait for the DOM to be fully rendered
    const rafId = requestAnimationFrame(() => {
      autoFocusInputRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);

  return {
    autoFocusInputRef,
    collisionError,
    form,
    handleDelete,
    isDeleted,
    isDeleting: deleteMutation.isPending,
    isEditMode,
    isLoading,
    isPending,
    markDuplicateCheckFieldTouched,
    mutationError,
    queryError,
  };
}
